import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, Rocket, Save, ShieldCheck, ShieldAlert, Repeat, FileText } from "lucide-react";
import { TaskExecution } from "@/pages/MyWork";

import { useI18n } from "@/context/I18nContext";

const CAMP_STATUS: Record<string, string> = { draft: "info", published: "good", archived: "inactive" };
const PRIORITY_MAP: Record<string, string> = { normal: "info", important: "warn", urgent: "critical" };
const PROG_MAP: Record<string, string> = {
  upcoming: "info", available: "info", in_progress: "warn", blocked: "critical",
  awaiting_verification: "warn", completed: "good", correction_required: "critical",
  overdue: "critical", cancelled: "inactive",
};

export default function Tasks() {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const canManage = hasPerm("task.manage");
  const canExecute = hasPerm("task.execute");
  const [tab, setTab] = useState("campaigns");
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [active, setActive] = useState<any>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/tasks/campaigns").then((r) => setCampaigns(r.data.campaigns)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    (<div>
      <PageHeader
        title="Tasks & Campaigns"
        subtitle="Templates, targeted checklists, recurrence, store execution and verification"
        testId="tasks-header"
        actions={canManage && <Button onClick={() => setComposeOpen(true)} data-testid="task-new-campaign"><Plus size={16} className="mr-1.5" />{t("New campaign")}</Button>}
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="campaigns" data-testid="tasks-tab-campaigns">{t("Campaigns")}</TabsTrigger>
          {canManage && <TabsTrigger value="templates" data-testid="tasks-tab-templates">{t("Templates")}</TabsTrigger>}
          {canExecute && <TabsTrigger value="execute" data-testid="tasks-tab-execute">{t("My Work")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="campaigns">
          {loading ? <LoadingBlock /> : campaigns.length === 0 ? (
            <EmptyState icon={<ClipboardList size={28} />} title="No campaigns yet"
              description={canManage ? t("Create a campaign to roll a checklist out to your stores.") : t("Published campaigns will appear here.")} />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {campaigns.map((c) => (
                <Card key={c.id} className="cursor-pointer p-4 hover:border-brand/40" onClick={() => setActive(c)} data-testid={`campaign-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 font-display font-semibold"><ClipboardList size={16} className="text-brand" />{c.title}</div>
                    <StatusPill status={CAMP_STATUS[c.status] || "info"}>{stat(c.status)}</StatusPill>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <StatusPill status={PRIORITY_MAP[c.priority] || "info"}>{stat(c.priority)}</StatusPill>
                    <span>{(c.template_snapshot?.checklist || []).length}{" "}{t("steps")}</span>
                    {c.schedule?.type === "recurring" && <span className="flex items-center gap-1"><Repeat size={12} />{c.schedule.recurrence?.freq}</span>}
                    {c.template_snapshot?.verification_rule !== "none" && <span>{t("· verify")}</span>}
                  </div>
                  {c.rollup && c.status === "published" && (
                    <div className="mt-3">
                      <div className="mb-1 flex justify-between text-[11px] text-muted-foreground">
                        <span>{c.rollup.done}/{c.rollup.total_instances}{" "}{t("done")}{c.rollup.overdue ? ` · ${c.rollup.overdue} overdue` : ""}</span>
                        <span>{c.rollup.pct}%</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-full bg-brand" style={{ width: `${c.rollup.pct}%` }} />
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {canManage && <TabsContent value="templates"><TemplatesPanel /></TabsContent>}
        {canExecute && <TabsContent value="execute"><TaskExecution /></TabsContent>}
      </Tabs>
      {canManage && <ComposeDialog open={composeOpen} setOpen={setComposeOpen} onDone={load} />}
      <CampaignDialog campaign={active} canManage={canManage} onClose={() => setActive(null)} onChange={load} />
    </div>)
  );
}

const TemplatesPanel: React.FC = () => {
  const {
    t: tI18n
  } = useI18n();

  const [templates, setTemplates] = useState<any[]>([]);
  useEffect(() => { api.get("/tasks/templates?include_archived=true").then((r) => setTemplates(r.data.templates)).catch(() => {}); }, []);
  if (templates.length === 0) return <EmptyState icon={<FileText size={28} />} title="No templates" description="Templates you save while composing a campaign appear here." />;
  return (
    (<div className="grid gap-3 md:grid-cols-2" data-testid="templates-list">
      {templates.map((t) => (
        <Card key={t.id} className="p-4" data-testid={`template-${t.id}`}>
          <div className="flex items-center justify-between">
            <div className="font-display font-semibold">{t.title}</div>
            <StatusPill status={t.active ? "good" : "inactive"}>{t.active ? "active" : tI18n("archived")}</StatusPill>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{stat(t.category)}· {(t.checklist || []).length}{" "}{tI18n("steps · verify:")}{" "}{t.verification_rule}{t.evidence_required ? tI18n("· evidence") : ""}{t.estimated_minutes ? ` · ~${t.estimated_minutes}m` : ""}</div>
        </Card>
      ))}
    </div>)
  );
};

const CampaignDialog: React.FC<{ campaign: any; canManage: boolean; onClose: () => void; onChange: () => void }> = ({ campaign, canManage, onClose, onChange }) => {
  const {
    t
  } = useI18n();

  const [progress, setProgress] = useState<any[]>([]);
  const [checklist, setChecklist] = useState<any[]>([]);
  const [rule, setRule] = useState<string>("none");
  const [busy, setBusy] = useState(false);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const loadProgress = useCallback(() => {
    if (!campaign || !canManage || campaign.status !== "published") return;
    api.get(`/tasks/campaigns/${campaign.id}/progress`).then((r) => { setProgress(r.data.progress); setChecklist(r.data.checklist); setRule(r.data.verification_rule); }).catch(() => {});
  }, [campaign, canManage]);
  useEffect(() => { loadProgress(); }, [loadProgress]);

  if (!campaign) return null;
  const snap = campaign.template_snapshot || {};

  const publish = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tasks/campaigns/${campaign.id}/publish`, {});
      toast.success(`${tl("Published to {sc} stores · {ic} instances", { sc: r.data.store_count, ic: r.data.instances_created })}`);
      onChange(); onClose();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };
  const archive = async () => {
    try { await api.post(`/tasks/campaigns/${campaign.id}/archive`, {}); toast.success("Campaign archived"); onChange(); onClose(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approve = async (instId: string) => {
    try { await api.post(`/tasks/instances/${instId}/verify`, { approve: true }); toast.success("Verified"); loadProgress(); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const confirmReject = async () => {
    if (!rejectId || !rejectReason.trim()) { toast.error("A reason is required"); return; }
    try {
      await api.post(`/tasks/instances/${rejectId}/verify`, { approve: false, reason: rejectReason.trim() });
      toast.success("Sent back for correction");
      setRejectId(null); setRejectReason(""); loadProgress(); onChange();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    (<Dialog open={!!campaign} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="campaign-dialog">
        <DialogHeader><DialogTitle>{campaign.title}</DialogTitle>
          <DialogDescription>{t("Campaign detail, checklist, store instance progress and verification.")}</DialogDescription></DialogHeader>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto">
          {snap.instructions && <p className="text-sm text-muted-foreground">{snap.instructions}</p>}
          <div className="text-xs text-muted-foreground">
            {campaign.schedule?.type === "recurring"
              ? tl("Recurring {freq} from {date}", { freq: campaign.schedule.recurrence?.freq, date: campaign.schedule.start_date })
              : tl("One-time · start {date}", { date: campaign.schedule?.start_date })}
            {campaign.schedule?.due_time ? ` · ${tl("due")} ${campaign.schedule.due_time}` : ""}{" "}{t("· verify:")}{" "}{snap.verification_rule}
            {snap.evidence_required ? t("· evidence required") : ""}
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{t("Checklist")}</div>
            {(snap.checklist || []).map((i: any) => (
              <div key={i.item_id} className="text-sm">• {i.text}{!i.required && <span className="ml-1 text-[11px] text-muted-foreground">{t("(optional)")}</span>}</div>
            ))}
          </div>

          {canManage && campaign.status === "draft" && (
            <Button onClick={publish} disabled={busy} data-testid="campaign-publish"><Rocket size={15} className="mr-1.5" />{t("Publish campaign")}</Button>
          )}

          {canManage && campaign.status === "published" && (
            <>
              {campaign.rollup && (
                <div className="grid grid-cols-3 gap-2 text-center text-xs" data-testid="campaign-rollup">
                  {["available", "in_progress", "awaiting_verification", "completed", "overdue", "correction_required"].map((k) => (
                    <div key={k} className="rounded-md border border-border p-2">
                      <div className="font-display text-lg font-bold">{campaign.rollup[k] || 0}</div>
                      <div className="text-muted-foreground">{k.replace(/_/g, " ")}</div>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("Store instances")}</div>
                {progress.length === 0 ? <div className="text-sm text-muted-foreground">{t("No instances in your scope.")}</div> : (
                  <div className="space-y-2" data-testid="campaign-progress-list">
                    {progress.map((p) => {
                      const doneCount = (p.completed_item_ids || []).length;
                      const canVerify = ["awaiting_verification", "completed", "correction_required"].includes(p.status);
                      return (
                        (<div key={p.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-2.5" data-testid={`progress-${p.id}`}>
                          <div>
                            <div className="text-sm font-medium">{t("Store")}{" "}{p.store_number}· {p.store_name}</div>
                            <div className="text-xs text-muted-foreground">{doneCount}/{checklist.length}{" "}{t("steps ·")}{" "}{p.occurrence_date}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusPill status={PROG_MAP[p.status] || "info"}>{p.status.replace(/_/g, " ")}</StatusPill>
                            {canVerify && (
                              <>
                                <Button size="sm" onClick={() => approve(p.id)} data-testid={`verify-approve-${p.id}`}><ShieldCheck size={14} className="mr-1" />{t("Verify")}</Button>
                                <Button size="sm" variant="outline" onClick={() => setRejectId(p.id)} data-testid={`verify-reject-${p.id}`}><ShieldAlert size={14} /></Button>
                              </>
                            )}
                          </div>
                        </div>)
                      );
                    })}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={archive} data-testid="campaign-archive">{t("Archive campaign")}</Button>
            </>
          )}
        </div>

        <Dialog open={!!rejectId} onOpenChange={(v) => { if (!v) { setRejectId(null); setRejectReason(""); } }}>
          <DialogContent data-testid="reject-dialog">
            <DialogHeader><DialogTitle>{t("Send back for correction")}</DialogTitle>
              <DialogDescription>{t("Provide a reason; the store will see it and can resubmit.")}</DialogDescription></DialogHeader>
            <div>
              <Label>{t("Reason")}</Label>
              <Textarea className="mt-1" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t("What needs to be corrected?")} data-testid="reject-reason" />
            </div>
            <DialogFooter><Button variant="destructive" onClick={confirmReject} data-testid="reject-confirm">{t("Send back")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>)
  );
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const ComposeDialog: React.FC<{ open: boolean; setOpen: (v: boolean) => void; onDone: () => void }> = ({ open, setOpen, onDone }) => {
  const {
    t: tI18n
  } = useI18n();

  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [priority, setPriority] = useState("normal");
  const [verification, setVerification] = useState("none");
  const [evidence, setEvidence] = useState(false);
  const [scope, setScope] = useState("tenant");
  const [stores, setStores] = useState<any[]>([]);
  const [items, setItems] = useState<string[]>([""]);
  const [scheduleType, setScheduleType] = useState("one_time");
  const [startDate, setStartDate] = useState(todayStr());
  const [dueTime, setDueTime] = useState("");
  const [freq, setFreq] = useState("daily");
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) api.get("/org/stores").then((r) => setStores(r.data.stores)).catch(() => {}); }, [open]);

  const reset = () => {
    setTitle(""); setInstructions(""); setPriority("normal"); setVerification("none"); setEvidence(false);
    setScope("tenant"); setItems([""]); setScheduleType("one_time"); setStartDate(todayStr()); setDueTime("");
    setFreq("daily"); setSaveTemplate(false);
  };

  const buildPayload = () => {
    const checklist = items.map((t) => t.trim()).filter(Boolean).map((t) => ({ text: t, required: true }));
    const audience = scope === "tenant" ? { scope_type: "tenant" } : { scope_type: "store", scope_id: scope };
    const schedule: any = { type: scheduleType, start_date: startDate, due_time: dueTime || null };
    if (scheduleType === "one_time") schedule.one_time_due_date = startDate;
    else schedule.recurrence = { freq, interval: 1, weekdays: [], horizon_days: 7 };
    return { title, instructions, priority, verification_rule: verification, evidence_required: evidence, audience, checklist, schedule };
  };

  const validate = () => {
    if (!title) { toast.error("Title required"); return false; }
    if (items.map((t) => t.trim()).filter(Boolean).length === 0) { toast.error("Add at least one step"); return false; }
    return true;
  };

  const create = async (publish: boolean) => {
    if (!validate()) return;
    setBusy(true);
    try {
      if (saveTemplate) {
        await api.post("/tasks/templates", {
          title, instructions, category: "General", verification_rule: verification, evidence_required: evidence,
          checklist: items.map((t) => t.trim()).filter(Boolean).map((t) => ({ text: t, required: true })),
        }).catch(() => {});
      }
      const r = await api.post("/tasks/campaigns", buildPayload());
      if (publish) { await api.post(`/tasks/campaigns/${r.data.id}/publish`, {}); toast.success("Campaign published"); }
      else toast.success("Draft saved");
      setOpen(false); reset(); onDone();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    (<Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="task-compose-dialog">
        <DialogHeader><DialogTitle>{tI18n("New campaign")}</DialogTitle>
          <DialogDescription>{tI18n(
            "Define the checklist, audience, schedule and verification, then save a draft or publish."
          )}</DialogDescription></DialogHeader>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto">
          <div><Label>{tI18n("Title")}</Label><Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-title" /></div>
          <div><Label>{tI18n("Instructions")}</Label><Textarea className="mt-1" value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="task-description" /></div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{tI18n("Priority")}</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">{tI18n("Normal")}</SelectItem><SelectItem value="important">{tI18n("Important")}</SelectItem><SelectItem value="urgent">{tI18n("Urgent")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{tI18n("Verification")}</Label>
              <Select value={verification} onValueChange={setVerification}>
                <SelectTrigger className="mt-1" data-testid="task-verification"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{tI18n("None")}</SelectItem>
                  <SelectItem value="any_manager">{tI18n("Any manager")}</SelectItem>
                  <SelectItem value="different_person">{tI18n("Different person (SoD)")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>{tI18n("Audience")}</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="mt-1" data-testid="task-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tenant">{tI18n("Entire organization")}</SelectItem>
                {stores.map((s) => <SelectItem key={s.id} value={s.id}>{tI18n("Store")}{" "}{s.store_number}· {s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{tI18n("Schedule")}</Label>
              <Select value={scheduleType} onValueChange={setScheduleType}>
                <SelectTrigger className="mt-1" data-testid="task-schedule-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="one_time">{tI18n("One-time")}</SelectItem><SelectItem value="recurring">{tI18n("Recurring")}</SelectItem></SelectContent>
              </Select>
            </div>
            {scheduleType === "recurring" && (
              <div>
                <Label>{tI18n("Frequency")}</Label>
                <Select value={freq} onValueChange={setFreq}>
                  <SelectTrigger className="mt-1" data-testid="task-freq"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="daily">{tI18n("Daily")}</SelectItem><SelectItem value="weekly">{tI18n("Weekly")}</SelectItem></SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{tI18n("Start date")}</Label><Input type="date" className="mt-1" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="task-start-date" /></div>
            <div><Label>{tI18n("Due time")}</Label><Input type="time" className="mt-1" value={dueTime} onChange={(e) => setDueTime(e.target.value)} data-testid="task-due-time" /></div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div><div className="text-sm font-medium">{tI18n("Require photo/file evidence")}</div><div className="text-xs text-muted-foreground">{tI18n("Stores must attach evidence to submit")}</div></div>
            <Switch checked={evidence} onCheckedChange={setEvidence} data-testid="task-evidence-required" />
          </div>

          <div>
            <Label>{tI18n("Checklist steps")}</Label>
            <div className="mt-1 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input value={it} placeholder={tl("Step {n}", { n: idx + 1 })} onChange={(e) => setItems((p) => p.map((x, i) => (i === idx ? e.target.value : x)))} data-testid={`task-step-${idx}`} />
                  {items.length > 1 && <Button variant="outline" size="icon" onClick={() => setItems((p) => p.filter((_, i) => i !== idx))} data-testid={`task-step-remove-${idx}`}><Trash2 size={15} /></Button>}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setItems((p) => [...p, ""])} data-testid="task-add-step"><Plus size={14} className="mr-1" />{tI18n("Add step")}</Button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch checked={saveTemplate} onCheckedChange={setSaveTemplate} data-testid="task-save-template" />{tI18n("Also save as a reusable template")}</label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => create(false)} disabled={busy} data-testid="task-save-draft"><Save size={15} className="mr-1.5" />{tI18n("Save draft")}</Button>
          <Button onClick={() => create(true)} disabled={busy} data-testid="task-create"><Rocket size={15} className="mr-1.5" />{tI18n("Publish")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};
